import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";

// Grid with 16:9 aspect ratio: 32 columns x 18 rows (32/18 = 16/9)
const GRID_WIDTH = 32;
const GRID_HEIGHT = 18;
const GAP = 1;
const PADDING = 1;

const DIRECTIONS = {
   UP: { x: 0, y: -1 },
   DOWN: { x: 0, y: 1 },
   LEFT: { x: -1, y: 0 },
   RIGHT: { x: 1, y: 0 },
};

export default function Snake() {
   const [snake, setSnake] = useState([{ x: 16, y: 9 }]);
   const [food, setFood] = useState({ x: 20, y: 10 });
   const [direction, setDirection] = useState(DIRECTIONS.RIGHT);
   const [gameOver, setGameOver] = useState(false);
   const [score, setScore] = useState(0);
   const directionRef = useRef(DIRECTIONS.RIGHT);
   const foodRef = useRef({ x: 20, y: 10 });

   // Generate random food position
   const generateFood = () => {
      const newFood = {
         x: Math.floor(Math.random() * GRID_WIDTH),
         y: Math.floor(Math.random() * GRID_HEIGHT),
      };
      return newFood;
   };

   // Auto-play logic - choose direction based on simple heuristics
   const chooseDirection = (head, currentDirection, foodPos, snakeBody) => {
      const directions = [
         { dir: DIRECTIONS.UP, pos: { x: head.x, y: head.y - 1 } },
         { dir: DIRECTIONS.DOWN, pos: { x: head.x, y: head.y + 1 } },
         { dir: DIRECTIONS.LEFT, pos: { x: head.x - 1, y: head.y } },
         { dir: DIRECTIONS.RIGHT, pos: { x: head.x + 1, y: head.y } },
      ];

      // Filter out opposite direction, out-of-bounds, and self-collisions
      const validDirections = directions.filter(({ dir, pos }) => {
         // Don't reverse direction
         if (dir.x === -currentDirection.x && dir.y === -currentDirection.y) {
            return false;
         }
         // Check bounds
         if (pos.x < 0 || pos.x >= GRID_WIDTH || pos.y < 0 || pos.y >= GRID_HEIGHT) {
            return false;
         }
         // Check self collision (avoid moving into snake body)
         if (snakeBody.some((segment) => segment.x === pos.x && segment.y === pos.y)) {
            return false;
         }
         return true;
      });

      if (validDirections.length === 0) {
         return currentDirection;
      }

      // Check if current direction is still valid and leads towards food
      const canGoStraight = validDirections.some(
         ({ dir }) => dir.x === currentDirection.x && dir.y === currentDirection.y
      );

      // Calculate distances to food and check if straight path is good
      const distances = validDirections.map(({ dir, pos }) => {
         const distance = Math.abs(pos.x - foodPos.x) + Math.abs(pos.y - foodPos.y);
         const isStraight = dir.x === currentDirection.x && dir.y === currentDirection.y;
         const isTowardsFood = 
            (foodPos.x > head.x && dir.x > 0) ||
            (foodPos.x < head.x && dir.x < 0) ||
            (foodPos.y > head.y && dir.y > 0) ||
            (foodPos.y < head.y && dir.y < 0);
         
         return {
            dir,
            distance,
            isStraight,
            isTowardsFood,
            score: distance - (isStraight ? 2 : 0) - (isTowardsFood ? 1 : 0),
         };
      });

      // Sort by score (lower is better - closer, straight, towards food)
      distances.sort((a, b) => a.score - b.score);

      // Prefer straight line if it's a good option (within top 2 choices)
      const bestStraight = distances.find((d) => d.isStraight && d.score <= distances[1]?.score);
      if (bestStraight && Math.random() < 0.85) {
         return bestStraight.dir;
      }

      // Otherwise choose best option (90% best, 10% second best for variety)
      if (Math.random() < 0.9) {
         return distances[0].dir;
      } else if (distances.length > 1) {
         return distances[1].dir;
      }
      
      return distances[0].dir;
   };

   useEffect(() => {
      foodRef.current = food;
   }, [food]);

   useEffect(() => {
      directionRef.current = direction;
   }, [direction]);

   useEffect(() => {
      if (gameOver) return;

      const gameInterval = setInterval(() => {
         setSnake((prevSnake) => {
            const head = prevSnake[0];
            const currentDir = directionRef.current;
            const currentFood = foodRef.current;
            
            // Choose new direction (pass snake body to avoid collisions)
            const newDir = chooseDirection(head, currentDir, currentFood, prevSnake);
            setDirection(newDir);
            directionRef.current = newDir;

            // Move snake
            const newHead = {
               x: head.x + newDir.x,
               y: head.y + newDir.y,
            };

            // Check wall collision
            if (
               newHead.x < 0 ||
               newHead.x >= GRID_WIDTH ||
               newHead.y < 0 ||
               newHead.y >= GRID_HEIGHT
            ) {
               setGameOver(true);
               return prevSnake;
            }

            // Check self collision
            if (prevSnake.some((segment) => segment.x === newHead.x && segment.y === newHead.y)) {
               setGameOver(true);
               return prevSnake;
            }

            const newSnake = [newHead, ...prevSnake];

            // Check food collision
            if (newHead.x === currentFood.x && newHead.y === currentFood.y) {
               setScore((prev) => prev + 1);
               const newFood = generateFood();
               setFood(newFood);
               foodRef.current = newFood;
               return newSnake;
            } else {
               newSnake.pop();
               return newSnake;
            }
         });
         }, 40);

      return () => clearInterval(gameInterval);
   }, [gameOver]);

   // Speed: 150ms / 4 = 37.5ms, rounded to 40ms for smoother gameplay

   // Reset game when it ends
   useEffect(() => {
      if (gameOver) {
         const resetTimeout = setTimeout(() => {
            setSnake([{ x: 16, y: 9 }]);
            const newFood = generateFood();
            setFood(newFood);
            foodRef.current = newFood;
            setDirection(DIRECTIONS.RIGHT);
            directionRef.current = DIRECTIONS.RIGHT;
            setGameOver(false);
            setScore(0);
         }, 2000);

         return () => clearTimeout(resetTimeout);
      }
   }, [gameOver]);

   return (
      <Box
         sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            justifyContent: "stretch",
            backgroundColor: "#000",
            position: "relative",
         }}
      >
         {/* Score */}
         <Box
            sx={{
               position: "absolute",
               top: 20,
               left: "50%",
               transform: "translateX(-50%)",
               color: "#fff",
               fontSize: "48px",
               fontWeight: "bold",
               fontFamily: "monospace",
               zIndex: 1000,
            }}
         >
            Score: {score}
         </Box>

         {/* Game Over */}
         {gameOver && (
            <Box
               sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  color: "#ff0000",
                  fontSize: "72px",
                  fontWeight: "bold",
                  fontFamily: "monospace",
                  zIndex: 2000,
               }}
            >
               GAME OVER
            </Box>
         )}

         {/* Game Grid */}
         <Box
            sx={{
               display: "grid",
               gridTemplateColumns: `repeat(${GRID_WIDTH}, 1fr)`,
               gridTemplateRows: `repeat(${GRID_HEIGHT}, 1fr)`,
               gap: `${GAP}px`,
               backgroundColor: "#111",
               padding: `${PADDING}px`,
               position: "absolute",
               top: 0,
               left: 0,
               right: 0,
               bottom: 0,
               width: "100%",
               height: "100%",
               aspectRatio: "16/9",
               border: "1px solid #ff0000",
            }}
         >
            {Array.from({ length: GRID_WIDTH * GRID_HEIGHT }).map((_, index) => {
               const x = index % GRID_WIDTH;
               const y = Math.floor(index / GRID_WIDTH);
               const isSnake = snake.some((segment) => segment.x === x && segment.y === y);
               const isHead = snake[0]?.x === x && snake[0]?.y === y;
               const isFood = food.x === x && food.y === y;

               return (
                  <Box
                     key={index}
                     sx={{
                        width: "100%",
                        height: "100%",
                        backgroundColor: isHead
                           ? "#00ff00"
                           : isSnake
                           ? "#00cc00"
                           : isFood
                           ? "#ff0000"
                           : "#222",
                        borderRadius: isFood ? "50%" : isHead ? "2px" : "1px",
                        border: isHead ? "1px solid #00ffff" : "none",
                     }}
                  />
               );
            })}
         </Box>
      </Box>
   );
}
